/**
 * Dependencies.
 */
import { debounce, orderBy } from 'lodash-es';
import { Component, Mixins } from 'vue-property-decorator';

import applicationConstant from '@/modules/main/constants/application.constant';
import ChooseChange from '@/modules/tracking/components/delivery/choose-change/choose-change.component.vue';
import ChooseDay from '@/modules/tracking/components/delivery/choose-day/choose-day.component.vue';
import ConfirmDay from '@/modules/tracking/components/delivery/confirm-day/confirm-day.component.vue';
import MapTrackingVehicle from '@/modules/tracking/components/map/tracking-vehicle.component.vue';
import StatusCard from '@/modules/tracking/components/status/card/status-card.component.vue';
import {
  ORDER_REQUEST_STATUS,
  ORDER_REQUEST_TYPES,
  TRACKING_STATUS
} from '@/modules/tracking/constants';
import TimelineMixin from '@/modules/tracking/mixins/timeline.mixin';
import TrackingMixin from '@/modules/tracking/mixins/tracking.mixin';
import { trackingStore } from '@/modules/tracking/store';
import configuration from '@/plugins/configuration.plugin';
import Error from '@/ui/components/error.component.vue';
import NotifyClientRequest from '@/ui/components/notify/client-request.component.vue';
import Timeline from '@/ui/components/timeline/timeline.component.vue';

/**
 * Component.
 */
@Component({
  components: {
    ChooseChange,
    ChooseDay,
    ConfirmDay,
    Error,
    MapTrackingVehicle,
    NotifyClientRequest,
    StatusCard,
    Timeline
  },
  name: 'choose-address-day-scene'
})
export default class extends Mixins(TrackingMixin, TimelineMixin) {
  /**
   * Property `firstTwoBlockElementsHeight`.
   */
  firstTwoBlockElementsHeight = 108;

  /**
   * Property `sheetInitialHeight`.
   */
  sheetInitialHeight = 0;

  /**
   * Property `statusElementHeight`.
   */
  statusElementHeight = 0;

  /**
   * Property `sheetExpandedHeight`.
   */
  sheetExpandedHeight = 0;

  /**
   * Property `dayChanged`.
   */
  dayChanged = null;

  /**
   * Property `isBottomSheetVisible`.
   */
  isBottomSheetVisible = true;

  /**
   * Property `isBottomSheetExpanded`.
   */
  isBottomSheetExpanded = false;

  /**
   * Property `isChoosingChangingDelivery`.
   */
  isChoosingChangingDelivery = true;

  /**
   * Property `isChoosingDeliveryDay`.
   */
  isChoosingDeliveryDay = false;

  /**
   * Property `isConfirmingDeliveryDay`.
   */
  isConfirmingDeliveryDay = false;

  /**
   * Property `windowHeight`.
   */
  windowHeight = null;

  /**
   * Property `maximumOfRequets`.
   */
  maximumOfRequets = null;

  /**
   * Getter `totalRequestsMade`.
   */
  get totalRequestsMade() {
    const requests = trackingStore.requests || [];
    const contactRequests = requests.filter(
      ({ status: { code } = { code: null }, type }) =>
        (type === ORDER_REQUEST_TYPES.CHANGE_DATE ||
          type === ORDER_REQUEST_TYPES.CHANGE_ADDRESS ||
          type === ORDER_REQUEST_TYPES.CHANGE_DATE_AND_ADDRESS) &&
        (code === ORDER_REQUEST_STATUS.AUTOMATIC_PENDING_TREATMENT ||
          code === ORDER_REQUEST_STATUS.TREATED)
    );

    return contactRequests.length;
  }

  /**
   * Getter `maximumRequestsReached`.
   */
  get maximumRequestsReached() {
    return this.totalRequestsMade >= this.maximumOfRequets;
  }

  /**
   * Getter `betweenFromDate`.
   */
  get betweenFromDate() {
    return trackingStore.betweenFromDate;
  }

  /**
   * Getter `betweenToDate`.
   */
  get betweenToDate() {
    return trackingStore.betweenToDate;
  }

  /**
   * Getter `deliveryCoordinates`.
   */
  get deliveryCoordinates() {
    const { address } = this.order || {};
    const { latitude = null, longitude = null } = address || {};

    return { latitude, longitude };
  }

  /**
   * Getter `vehicleCoordinates`.
   */
  get vehicleCoordinates() {
    return trackingStore.vehicleCoordinates;
  }

  /**
   * Getter distributionCenter.
   */
  get distributionCenter() {
    return trackingStore.distributionCenter?.name;
  }

  /**
   * Getter driver.
   */
  get driver() {
    return trackingStore.driver;
  }

  /**
   * Getter `servicePoint`.
   */
  get servicePoint() {
    const { address } = this.order || {};
    const { servicePoint } = address || {};
    const { name = null } = servicePoint || {};

    return name;
  }

  /**
   * Getter vehicle.
   */
  get vehicle() {
    return trackingStore.vehicle;
  }

  /**
   * Getter is status processing.
   */
  get isStatusProcessing() {
    const { PROCESSING } = this.trackingStatus;
    const { status: { code = null } = {} } = this.currentTracking || {};

    return code === PROCESSING;
  }

  /**
   * Getter on the way to service point.
   */
  get onTheWayToServicePoint() {
    const { address = null } = this.order || {};
    const { servicePoint = false } = address || {};
    const onTheWayToServicePoint = !!servicePoint;

    return onTheWayToServicePoint;
  }

  /**
   * Getter is status delivered.
   */
  get isStatusSuccessful() {
    const { COLLECTED, DELIVERED } = this.trackingStatus;
    const { status: { code = null } = {} } = this.currentTracking || {};

    return code === DELIVERED || code === COLLECTED;
  }

  /**
   * Getter is status unsuccess.
   */
  get isStatusUnsuccess() {
    const {
      CANCELED,
      IN_TREATMENT,
      NOT_COLLECTED,
      NOT_DELIVERED,
      RETURNED
    } = this.trackingStatus;
    const { status: { code = null } = {} } = this.currentTracking || {};

    return (
      code === NOT_COLLECTED ||
      code === NOT_DELIVERED ||
      code === IN_TREATMENT ||
      code === RETURNED ||
      code === CANCELED
    );
  }

  /**
   * Getter map height.
   */
  get mapHeight() {
    return `calc(100vh - ${this.firstTwoBlockElementsHeight}px - ${this.statusElementHeight}px - ${this.sheetInitialHeight}px + 8px)`;
  }

  /**
   * Getter packages expiration date.
   */
  get packagesExpirationDate() {
    let packagesExpirationDate = null;

    if (this.isDelivery) {
      let { packages = [] } = trackingStore.delivery || {};

      packages = orderBy(packages, ['expirationDate'], ['asc']);

      const deliveryPackage = packages.find(
        ({ expirationDate = null }) => !!expirationDate
      );
      const { expirationDate = null } = deliveryPackage || {};

      packagesExpirationDate = expirationDate;
    }

    return packagesExpirationDate;
  }

  /**
   * Getter packages.
   */
  get packages() {
    let orderpackages = [];

    if (this.isDelivery) {
      const { packages = [] } = trackingStore.delivery || {};
      orderpackages = packages;
    }

    return orderpackages;
  }

  /**
   * Getter `expectedOrderDate`.
   */
  get expectedOrderDate() {
    let expectedOrderDate = null;

    if (this.isDelivery) {
      const { expectedDeliveryDate = null } = trackingStore.delivery || {};
      expectedOrderDate = expectedDeliveryDate;
    } else {
      const { expectedCollectDate = null } = trackingStore.collect || {};
      expectedOrderDate = expectedCollectDate;
    }

    return expectedOrderDate;
  }

  /**
   * Getter marker type.
   */
  get markerType() {
    let marker = 'inRouting';

    if (this.isStatusProcessing) {
      marker = 'processing';
    }

    if (this.onTheWayToServicePoint) {
      marker = 'onTheWayToServicePoint';
    }

    if (this.isStatusSuccessful) {
      marker = 'delivered';
    }

    if (this.isStatusUnsuccess) {
      marker = 'notDelivered';
    }

    return marker;
  }

  /**
   * Getter `vehicleType`.
   */
  get vehicleType() {
    let result: 'van' | 'moto' = null;

    const { vehicleType = null } = this.vehicle || {};
    const { code = null } = vehicleType || {};

    switch (code) {
      case 'MTC':
        result = 'moto';
        break;
      case 'VANNC':
        result = 'van';
        break;
      case 'VANNE':
        result = 'van';
        break;
      default:
        result = 'van';
        break;
    }

    return result;
  }

  /**
   * Getter support url.
   */
  get supportUrl() {
    const { address } = this.order || {};
    const { countryCode = '' } = address || {};
    const { lang: { currentLanguage: { code: languageCode = '' } = {} } = {} } =
      this.$kit || {};

    let { supportUrl = '' } = applicationConstant || {};

    supportUrl = supportUrl
      .replace('{store}', countryCode.toLocaleLowerCase())
      .replace('{language}', languageCode.toLocaleLowerCase());

    return supportUrl;
  }

  /**
   * Getter window resize debounced.
   */
  get windowResizeDebounced() {
    return debounce(this.handleWindowResize, 100);
  }

  /**
   * Getter `newDay`.
   */
  get newDay() {
    const date = trackingStore.dayChanged;

    return this.$moment(date).isValid() ? date : null;
  }

  /**
   * Getter fake tracking true state.
   */
  get fakeTrackingTrueState() {
    const { hideHistory = false } = this.currentTracking || {};

    let state = null;

    if (hideHistory) {
      const { NOT_COLLECTED, NOT_DELIVERED, PROCESSING } = TRACKING_STATUS;
      const [firstItem, secondItem] = this.trackingHistory;

      const { status: firstItemStatus } = firstItem || {};
      const { status: secondItemStatus } = secondItem || {};

      const { code: firstItemStatusCode } = firstItemStatus || {};
      const { code: secondItemStatusCode } = secondItemStatus || {};

      if (
        firstItemStatusCode === PROCESSING &&
        secondItemStatusCode === NOT_DELIVERED
      ) {
        state = NOT_DELIVERED;
      }

      if (
        firstItemStatusCode === PROCESSING &&
        secondItemStatusCode === NOT_COLLECTED
      ) {
        state = NOT_COLLECTED;
      }
    }

    return state;
  }

  /**
   * Getter delivery status.
   */
  get deliveryStatus() {
    const { fakeTrackingTrueState: fakeStatus } = this;
    const { status: { code: status = null } = {} } = this.currentTracking || {};

    return fakeStatus || status;
  }

  /**
   * Lifecycle created hook.
   */
  created() {
    this.handleGetConfiguration();
  }

  /**
   * Lifecycle mounted hook.
   */
  async mounted() {
    this.$on('trackingDataUpdated', () => {
      const isValid = this.handleIsValidToBeOnScene();

      if (!isValid) {
        this.handleForceTrackingScene();
      } else {
        this.handleWindowResize();
      }
    });

    await this.handleInitializeMixin();

    const isValid = this.handleIsValidToBeOnScene();

    if (!isValid) {
      this.handleForceTrackingScene();
    } else {
      this.handleWindowResize();

      window.addEventListener('resize', this.windowResizeDebounced);

      const { newDay = null } = this || {};

      if (newDay) {
        this.dayChanged = newDay;

        this.isChoosingChangingDelivery = false;
        this.isChoosingDeliveryDay = false;
        this.isConfirmingDeliveryDay = true;
      }
    }
  }

  /**
   * Lifecycle destroyed hook.
   */
  destroyed() {
    window.removeEventListener('resize', this.windowResizeDebounced);
  }

  /**
   * Handle get configuration.
   */
  async handleGetConfiguration() {
    const config = await configuration.getRequestMaximum();
    const { requestMaximum } = config || {};

    this.maximumOfRequets = requestMaximum;
  }

  /**
   * Handle is valid to be on scene.
   */
  handleIsValidToBeOnScene() {
    return this.canCreateChangeRequest && this.isDelivery;
  }

  /**
   * Handle bottom sheet click.
   */
  handleBottomSheetClick() {
    this.isBottomSheetExpanded = !this.isBottomSheetExpanded;
  }

  /**
   * Handle window resize.
   */
  handleWindowResize() {
    this.windowHeight = document.documentElement.clientHeight;
    this.firstTwoBlockElementsHeight = this.windowHeight <= 568 ? 76 : 108;
    this.isBottomSheetExpanded = false;

    this.$nextTick().then(() => {
      if (!this.isBottomSheetExpanded) {
        this.sheetExpandedHeight =
          (window?.innerHeight || 0) - this.firstTwoBlockElementsHeight;

        this.sheetInitialHeight = (this.$refs.sheet as Vue)?.$el?.clientHeight;
        this.statusElementHeight = (this.$refs
          .status as Vue)?.$el?.clientHeight;
      }
    });
  }

  /**
   * Handle sheet swipe.
   */
  handleSheetSwipe(direction) {
    switch (direction) {
      case 'up':
        this.isBottomSheetExpanded = true;
        break;

      default:
        this.isBottomSheetExpanded = false;
        break;
    }
  }

  /**
   * Handle cancel change delivery.
   */
  handleCancelChangeDelivery() {
    this.isChoosingChangingDelivery = false;

    trackingStore.setChangeDeliveryAddress({ address: null });
    trackingStore.setChangeDeliveryDay({ day: null });

    this.$router.push({
      name: 'Tracking',
      params: {
        key: this.key
      }
    });
  }

  /**
   * Handle change day
   */
  handleChangeDay() {
    this.isChoosingChangingDelivery = false;
    this.isChoosingDeliveryDay = true;
  }

  /**
   * Handle cancel change day.
   */
  handleCancelChooseDay() {
    this.dayChanged = null;

    trackingStore.setChangeDeliveryAddress({ address: null });
    trackingStore.setChangeDeliveryDay({ day: null });

    this.isChoosingChangingDelivery = true;
    this.isChoosingDeliveryDay = false;
  }

  /**
   * Handle update day
   */
  handleUpdateDay({ day }) {
    trackingStore.setChangeDeliveryDay({ day });

    this.dayChanged = day;

    this.isChoosingDeliveryDay = false;
    this.isConfirmingDeliveryDay = true;
  }

  /**
   * Handle confirm day.
   */
  handleConfirmDay() {
    this.$router.push({
      name: 'ConfirmChange',
      params: {
        key: this.key,
        type: 'day'
      }
    });
  }

  /**
   * Handle cancel confirm day.
   */
  handleCancelConfirmDay() {
    this.dayChanged = null;

    this.isChoosingChangingDelivery = false;
    this.isChoosingDeliveryDay = true;
    this.isConfirmingDeliveryDay = false;
  }
}
